// src/modules/app-views/omnichannel/pages/Init/components/slaHelpers.js
// *******************************************************************************
// OMNICHANNEL SLA HELPERS
// *******************************************************************************
// Reusable SLA calculation functions adapted from CRM KanbanCard.
// Computes countdown, colors, tooltips and icons for the ChatMenu third line.
// *******************************************************************************

import dayjs from 'dayjs';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// Fallback business schedule: Mon–Fri, 08:00–18:00
export const DEFAULT_BUSINESS_HOURS = {
  sunday: { enabled: false },
  monday: { enabled: true, open: '08:00', close: '18:00' },
  tuesday: { enabled: true, open: '08:00', close: '18:00' },
  wednesday: { enabled: true, open: '08:00', close: '18:00' },
  thursday: { enabled: true, open: '08:00', close: '18:00' },
  friday: { enabled: true, open: '08:00', close: '18:00' },
  saturday: { enabled: false },
};

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// *******************************************************************************
// MAIN FUNCTION — computeSlaState
// *******************************************************************************

/**
 * Compute the SLA visual state for a conversation.
 *
 * @param {Object} conversation — omnichannel conversation object
 * @param {Object} slaConfig — { attendance: { elapsedTime, businessTime }, response: { elapsedTime, businessTime } }
 * @returns {{ mode: string, label: string, color: string, tooltip: string, icon: 'clock'|'pause' } | null}
 */
export function computeSlaState(conversation, slaConfig) {
  if (!slaConfig || !conversation) return null;

  const isAssigned = !!conversation.assignedTo;
  const lastMsg = getLastMessage(conversation);
  const lastMsgIsInbound = lastMsg?.direction === 'inbound';
  const hasAgentMessage = hasAgentResponded(conversation);

  // ── STATE 1: In queue (no assignedTo) → SLA Attendance ──────────────────
  if (!isAssigned) {
    return computeAttendanceSla(conversation, slaConfig.attendance);
  }

  // ── STATE 2: Assigned, last message is outbound (agent sent) → Waiting for client ──
  if (!lastMsgIsInbound && hasAgentMessage) {
    return computeWaitingState(conversation);
  }

  // ── STATE 3: Assigned, last message is inbound (client sent) → SLA Response ──
  if (lastMsgIsInbound) {
    return computeResponseSla(conversation, slaConfig.response);
  }

  // Assigned but no messages yet or edge case
  return computeAttendanceSla(conversation, slaConfig.attendance);
}

// *******************************************************************************
// STATE BUILDERS
// *******************************************************************************

function computeAttendanceSla(conversation, config) {
  if (!config) return null;

  const limitMinutes = getActiveLimitMinutes(config);
  if (!limitMinutes) return null;

  const isBusiness = isBusinessMode(config);
  const schedule = DEFAULT_BUSINESS_HOURS;
  // Pagination may only return the last message; fall back to createdAt for the first-message timestamp
  const startAt = conversation.messages?.[0]?.timestamp || conversation.createdAt || conversation.lastMessageAt;
  if (!startAt) return null;

  if (isBusiness) {
    const bizSec = computeBusinessSecondsElapsed(startAt, schedule);
    const limitSec = limitMinutes * 60;
    const remainingSec = limitSec - bizSec;
    const pct = remainingSec / limitSec;
    const deadline = computeBusinessDeadline(startAt, limitMinutes, schedule);
    const inBiz = checkIsInBusinessHours(schedule);
    return {
      mode: 'attendance',
      label: formatDurationMs(remainingSec * 1_000),
      color: getSlaColor(pct),
      tooltip: deadline
        ? `${translateX('sla_attendance')}: ${dayjs(deadline).format('DD/MM/YY HH:mm')} | ${translateX('sla_business')} | ${inBiz ? translateX('time_running') : translateX('time_paused')}`
        : null,
      icon: inBiz ? 'clock' : 'pause',
    };
  }

  // Elapsed mode
  const elapsedMs = Math.max(0, Date.now() - new Date(startAt).getTime());
  const limitMs = limitMinutes * 60_000;
  const remainingMs = limitMs - elapsedMs;
  const pct = remainingMs / limitMs;
  const deadline = new Date(new Date(startAt).getTime() + limitMs);
  return {
    mode: 'attendance',
    label: formatDurationMs(remainingMs),
    color: getSlaColor(pct),
    tooltip: `${translateX('sla_attendance')}: ${dayjs(deadline).format('DD/MM/YY HH:mm')} | ${translateX('sla_elapsed')}`,
    icon: 'clock',
  };
}

function computeWaitingState(conversation) {
  const lastAgentMsg = getLastAgentMessage(conversation);
  const startAt = lastAgentMsg?.timestamp;
  if (!startAt) return null;

  const elapsedMs = Math.max(0, Date.now() - new Date(startAt).getTime());
  return {
    mode: 'waiting',
    label: `${translateX('awaiting_response')} ${formatDurationMs(elapsedMs)}`,
    color: '#8c8c8c',
    tooltip: `${translateX('last_agent_message')}: ${dayjs(startAt).format('DD/MM/YY HH:mm')}`,
    icon: 'clock',
  };
}

function computeResponseSla(conversation, config) {
  if (!config) return null;

  const limitMinutes = getActiveLimitMinutes(config);
  if (!limitMinutes) return null;

  const isBusiness = isBusinessMode(config);
  const schedule = DEFAULT_BUSINESS_HOURS;
  const lastInbound = getLastInboundMessage(conversation);
  const startAt = lastInbound?.timestamp;
  if (!startAt) return null;

  if (isBusiness) {
    const bizSec = computeBusinessSecondsElapsed(startAt, schedule);
    const limitSec = limitMinutes * 60;
    const remainingSec = limitSec - bizSec;
    const pct = remainingSec / limitSec;
    const deadline = computeBusinessDeadline(startAt, limitMinutes, schedule);
    const inBiz = checkIsInBusinessHours(schedule);
    return {
      mode: 'response',
      label: formatDurationMs(remainingSec * 1_000),
      color: getSlaColor(pct),
      tooltip: deadline
        ? `${translateX('sla_response')}: ${dayjs(deadline).format('DD/MM/YY HH:mm')} | ${translateX('sla_business')} | ${inBiz ? translateX('time_running') : translateX('time_paused')}`
        : null,
      icon: inBiz ? 'clock' : 'pause',
    };
  }

  // Elapsed mode
  const elapsedMs = Math.max(0, Date.now() - new Date(startAt).getTime());
  const limitMs = limitMinutes * 60_000;
  const remainingMs = limitMs - elapsedMs;
  const pct = remainingMs / limitMs;
  const deadline = new Date(new Date(startAt).getTime() + limitMs);
  return {
    mode: 'response',
    label: formatDurationMs(remainingMs),
    color: getSlaColor(pct),
    tooltip: `${translateX('sla_response')}: ${dayjs(deadline).format('DD/MM/YY HH:mm')} | ${translateX('sla_elapsed')}`,
    icon: 'clock',
  };
}

// *******************************************************************************
// CONVERSATION HELPERS
// *******************************************************************************

function getLastMessage(conversation) {
  const msgs = conversation.messages;
  return msgs?.length ? msgs[msgs.length - 1] : null;
}

function getLastAgentMessage(conversation) {
  const msgs = conversation.messages || [];
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i].sender?.type === 'agent' || msgs[i].direction === 'outbound') return msgs[i];
  }
  return null;
}

function getLastInboundMessage(conversation) {
  const msgs = conversation.messages || [];
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i].direction === 'inbound') return msgs[i];
  }
  return null;
}

function hasAgentResponded(conversation) {
  return (conversation.messages || []).some(
    m => m.sender?.type === 'agent' || m.direction === 'outbound'
  );
}

// *******************************************************************************
// SLA CONFIG HELPERS
// *******************************************************************************

function getActiveLimitMinutes(config) {
  if (config?.businessTime?.minutes > 0) return config.businessTime.minutes;
  if (config?.elapsedTime?.minutes > 0) return config.elapsedTime.minutes;
  return 0;
}

function isBusinessMode(config) {
  return config?.businessTime?.minutes > 0;
}

// *******************************************************************************
// BUSINESS HOURS CALCULATION (adapted from CRM KanbanCard)
// *******************************************************************************

export function checkIsInBusinessHours(businessHours, holidays) {
  if (!businessHours) return false;
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  const holidaySet = new Set((holidays || []).map(h => h.date));
  if (holidaySet.has(dateStr)) return false;
  const sched = businessHours[DAY_NAMES[now.getDay()]];
  if (!sched?.enabled || !sched.open || !sched.close) return false;
  const [openH, openM] = sched.open.split(':').map(Number);
  const [closeH, closeM] = sched.close.split(':').map(Number);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  return nowMinutes >= openH * 60 + openM && nowMinutes < closeH * 60 + closeM;
}

export function computeBusinessSecondsElapsed(inAt, businessHours, holidays) {
  if (!inAt || !businessHours) return 0;
  const start = new Date(inAt);
  const now = new Date();
  if (now <= start) return 0;

  const holidaySet = new Set((holidays || []).map(h => h.date));
  let totalMs = 0;
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);

  while (cursor <= now) {
    const sched = businessHours[DAY_NAMES[cursor.getDay()]];
    const dateStr = cursor.toISOString().split('T')[0];
    if (sched?.enabled && !holidaySet.has(dateStr) && sched.open && sched.close) {
      const [openH, openM] = sched.open.split(':').map(Number);
      const [closeH, closeM] = sched.close.split(':').map(Number);
      const dayOpen = new Date(cursor);
      dayOpen.setHours(openH, openM, 0, 0);
      const dayClose = new Date(cursor);
      dayClose.setHours(closeH, closeM, 0, 0);
      const overlapStart = Math.max(start.getTime(), dayOpen.getTime());
      const overlapEnd = Math.min(now.getTime(), dayClose.getTime());
      if (overlapEnd > overlapStart) totalMs += overlapEnd - overlapStart;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return Math.floor(totalMs / 1_000);
}

export function computeBusinessDeadline(inAt, limitMinutes, businessHours, holidays) {
  if (!inAt || !businessHours || !limitMinutes) return null;
  const start = new Date(inAt);
  const limitMs = limitMinutes * 60 * 1_000;
  const holidaySet = new Set((holidays || []).map(h => h.date));
  let accumulated = 0;
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  const maxDate = new Date(start);
  maxDate.setFullYear(maxDate.getFullYear() + 1);

  while (cursor <= maxDate) {
    const sched = businessHours[DAY_NAMES[cursor.getDay()]];
    const dateStr = cursor.toISOString().split('T')[0];
    if (sched?.enabled && !holidaySet.has(dateStr) && sched.open && sched.close) {
      const [openH, openM] = sched.open.split(':').map(Number);
      const [closeH, closeM] = sched.close.split(':').map(Number);
      const dayOpen = new Date(cursor);
      dayOpen.setHours(openH, openM, 0, 0);
      const dayClose = new Date(cursor);
      dayClose.setHours(closeH, closeM, 0, 0);
      const segStart = Math.max(start.getTime(), dayOpen.getTime());
      const segEnd = dayClose.getTime();
      if (segEnd > segStart) {
        const segMs = segEnd - segStart;
        if (accumulated + segMs >= limitMs) return new Date(segStart + (limitMs - accumulated));
        accumulated += segMs;
      }
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return null;
}

// *******************************************************************************
// VISUAL HELPERS
// *******************************************************************************

// *******************************************************************************
// META 24H WINDOW
// *******************************************************************************

const META_WINDOW_MS = 24 * 60 * 60 * 1_000; // 24 hours

/**
 * Compute the Meta 24h billing window state for a conversation.
 * The window opens when the last INBOUND message arrives and closes 24h later.
 *
 * @param {Object} conversation — omnichannel conversation object
 * @returns {{ open: boolean, remainingMs: number, label: string, tooltip: string }}
 */
export function computeMetaWindow(conversation) {
  if (!conversation) return { open: false, remainingMs: 0, label: '0h 0m', tooltip: '' };

  // Find last inbound message timestamp
  const msgs = conversation.messages || [];
  let lastInboundTs = null;
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i].direction === 'inbound') {
      lastInboundTs = msgs[i].timestamp;
      break;
    }
  }

  if (!lastInboundTs) {
    return { open: false, remainingMs: 0, label: '0h 0m', tooltip: 'window_closed' };
  }

  const elapsed = Date.now() - new Date(lastInboundTs).getTime();
  const remaining = META_WINDOW_MS - elapsed;

  if (remaining <= 0) {
    return { open: false, remainingMs: 0, label: '0h 0m', tooltip: 'window_closed' };
  }

  return {
    open: true,
    remainingMs: remaining,
    label: formatDurationMs(remaining),
    tooltip: 'window_temporarily_open',
  };
}

export function getSlaColor(pct) {
  if (pct > 0.75) return '#389e0d';
  if (pct > 0.5) return '#52c41a';
  if (pct > 0.25) return '#faad14';
  if (pct > 0) return '#fa8c16';
  return '#ff4d4f';
}

export function formatDurationMs(ms) {
  const sign = ms < 0 ? '-' : '';
  const abs = Math.abs(ms);
  const totalSec = Math.floor(abs / 1_000);
  const d = Math.floor(totalSec / 86_400);
  const h = Math.floor((totalSec % 86_400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (d > 0) return `${sign}${d}d ${h}h ${m}m`;
  if (h > 0) return `${sign}${h}h ${m}m`;
  return `${sign}${m}m`;
}
